import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { gatewayService } from '@/lib/gateway-service';
import { sectionRecordingService } from '@/lib/section-recording-service';
import { updateAppointmentScribeJobId } from '@/store/slices/dataSlice';
import {
  clearCompletedSection,
  clearSectionRecordingContext,
  pauseSectionRecording,
  resumeSectionRecording,
  SectionType,
  setSectionRecordingContext,
  startSectionRecording,
  stopSectionRecording,
} from '@/store/slices/sectionRecordingSlice';
import { useCallback, useEffect } from 'react';

/**
 * Format date of birth to MM/DD/YYYY format
 */
const formatDateOfBirth = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Use UTC methods to preserve the exact date
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date of birth:', error);
    return dateString;
  }
};

/**
 * Format appointment start time to MM/DD/YYYY HH:MM:SS format
 */
const formatAppointmentStartTime = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);

    // Use UTC methods directly
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Error formatting appointment start time:', error);
    // Return current UTC time as fallback
    const now = new Date();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const year = now.getUTCFullYear();
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  }
};

/**
 * Hook for managing section-based audio recording
 */
export const useSectionRecording = () => {
  const dispatch = useAppDispatch();
  const sectionRecordingState = useAppSelector(
    (state) => state.sectionRecording
  );
  const selectedAppointment = useAppSelector(
    (state) => state.data.appointments.selectedAppointment
  );
  const currentUser = useAppSelector((state) => state.auth.user);
  const selectedLocationId = useAppSelector(
    (state) => state.data.selectedLocation.locationId
  );

  /**
   * Initialize recording context when appointment is selected
   */
  useEffect(() => {
    if (selectedAppointment?.scribeJobId) {
      dispatch(
        setSectionRecordingContext({
          appointmentId: selectedAppointment.appointmentId,
          patientId: selectedAppointment.patientId,
          locationId: selectedAppointment.locationId,
          scribeJobId: selectedAppointment.scribeJobId,
        })
      );
    } else {
      dispatch(clearSectionRecordingContext());
    }
  }, [
    selectedAppointment?.scribeJobId,
    selectedAppointment?.appointmentId,
    selectedAppointment?.patientId,
    selectedAppointment?.locationId,
    dispatch,
  ]);

  /**
   * Ensure a scribe job exists for the selected appointment
   */
  const ensureScribeJob = useCallback(async (): Promise<string> => {
    // If scribe job already exists, return it
    if (selectedAppointment?.scribeJobId) {
      return selectedAppointment.scribeJobId;
    }

    // Check if we have all required data
    if (!selectedAppointment || !selectedLocationId || !currentUser?.id) {
      throw new Error('Missing required appointment or user data');
    }

    console.log('Creating scribe job for section recording...');

    try {
      // Create scribe job via gateway API (same as AiScribeAudioControls)
      const cloudFunctionResult = await gatewayService.createScribeJob({
        appointmentId: selectedAppointment.appointmentId.toString(),
        locationId: selectedLocationId.toString(),
        patientId: selectedAppointment.patientId.toString(),
        firstName: selectedAppointment.patientFirstName || '',
        lastName: selectedAppointment.patientLastName || '',
        dob: formatDateOfBirth(selectedAppointment.patientBirthDate || ''),
        gender: selectedAppointment.patientGender || '',
        service: selectedAppointment.service || '',
        appointmentStartTime: formatAppointmentStartTime(
          selectedAppointment.startTime || new Date().toISOString()
        ),
      });

      if (cloudFunctionResult.success && cloudFunctionResult.data) {
        const responseData = cloudFunctionResult.data as {
          scribeJobId: string;
        };

        if (responseData.scribeJobId) {
          // Update Redux with the new scribeJobId
          dispatch(
            updateAppointmentScribeJobId({
              appointmentId: selectedAppointment.appointmentId,
              scribeJobId: responseData.scribeJobId,
            })
          );

          console.log('✅ Created scribe job:', responseData.scribeJobId);
          return responseData.scribeJobId;
        } else {
          console.warn('⚠️ Cloud function response missing scribeJobId');
          throw new Error('Cloud function response missing scribeJobId');
        }
      } else {
        console.error(
          '❌ Failed to create AI Scribe job:',
          cloudFunctionResult.error
        );
        throw new Error(
          cloudFunctionResult.error || 'Failed to create scribe job'
        );
      }
    } catch (error) {
      console.error('Failed to call createAiScribeJob cloud function:', error);
      throw error;
    }
  }, [selectedAppointment, selectedLocationId, currentUser, dispatch]);

  /**
   * Start recording a section
   */
  const startRecording = useCallback(
    async (section: SectionType) => {
      try {
        // Ensure scribe job exists
        const scribeJobId = await ensureScribeJob();

        if (!scribeJobId || !selectedAppointment) {
          throw new Error('No scribe job available for recording');
        }

        // Ensure context is set immediately (don't wait for useEffect)
        const context = {
          appointmentId: selectedAppointment.appointmentId,
          patientId: selectedAppointment.patientId,
          locationId:
            selectedAppointment.locationId || selectedLocationId || '',
          scribeJobId: scribeJobId,
        };

        console.log('Setting section recording context:', context);
        dispatch(setSectionRecordingContext(context));

        // Start recording in Redux state
        dispatch(startSectionRecording(section));

        // Start recording in service
        await sectionRecordingService.startRecording(section);
      } catch (error) {
        console.error(`Failed to start recording section ${section}:`, error);
        throw error;
      }
    },
    [dispatch, ensureScribeJob, selectedAppointment, selectedLocationId]
  );

  /**
   * Pause recording the active section
   */
  const pauseRecording = useCallback(async () => {
    try {
      const activeSection = sectionRecordingState.activeRecording?.sectionName;
      if (!activeSection) {
        console.warn('No active recording to pause');
        return;
      }

      // Pause recording in service
      await sectionRecordingService.pauseRecording(activeSection);

      // Update Redux state
      dispatch(pauseSectionRecording());
    } catch (error) {
      console.error('Failed to pause recording:', error);
      throw error;
    }
  }, [dispatch, sectionRecordingState.activeRecording?.sectionName]);

  /**
   * Resume recording the active section
   */
  const resumeRecording = useCallback(async () => {
    try {
      const activeSection = sectionRecordingState.activeRecording?.sectionName;
      if (!activeSection) {
        console.warn('No active recording to resume');
        return;
      }

      // Resume recording in service
      await sectionRecordingService.resumeRecording(activeSection);

      // Update Redux state
      dispatch(resumeSectionRecording());
    } catch (error) {
      console.error('Failed to resume recording:', error);
      throw error;
    }
  }, [dispatch, sectionRecordingState.activeRecording?.sectionName]);

  /**
   * Stop recording and upload the active section
   */
  const stopRecording = useCallback(async () => {
    try {
      const activeSection = sectionRecordingState.activeRecording?.sectionName;
      if (!activeSection) {
        console.warn('No active recording to stop');
        return;
      }

      const createdBy = currentUser?.id || 'unknown';

      // Stop recording in Redux first (moves to recordedSections)
      dispatch(stopSectionRecording());

      // Stop recording and upload in service
      await sectionRecordingService.stopRecording(activeSection, createdBy);

      // Redux state is updated by the service via dispatch
    } catch (error) {
      console.error('Failed to stop recording:', error);
      throw error;
    }
  }, [
    dispatch,
    currentUser?.id,
    sectionRecordingState.activeRecording?.sectionName,
  ]);

  /**
   * Get status for a specific section
   */
  const getSectionStatus = useCallback(
    (section: SectionType) => {
      // Check if this is the active recording
      if (sectionRecordingState.activeRecording?.sectionName === section) {
        return sectionRecordingState.activeRecording.status;
      }
      // Otherwise it's idle
      return 'idle';
    },
    [sectionRecordingState.activeRecording]
  );

  /**
   * Get error for a specific section from recorded sections
   */
  const getSectionError = useCallback(
    (section: SectionType) => {
      // Find the most recent error for this section in recorded sections
      const recordedSection = sectionRecordingState.recordedSections
        .filter((s) => s.sectionName === section && s.error)
        .sort((a, b) => b.startTime - a.startTime)[0];

      return recordedSection?.error || null;
    },
    [sectionRecordingState.recordedSections]
  );

  /**
   * Check if any section is currently recording
   */
  const isAnyRecording = useCallback(() => {
    return (
      sectionRecordingState.activeRecording !== null &&
      (sectionRecordingState.activeRecording.status === 'recording' ||
        sectionRecordingState.activeRecording.status === 'paused')
    );
  }, [sectionRecordingState.activeRecording]);

  /**
   * Get the currently active section
   */
  const getActiveSection = useCallback(() => {
    return sectionRecordingState.activeRecording?.sectionName || null;
  }, [sectionRecordingState.activeRecording]);

  /**
   * Check if a section has completed recordings
   */
  const isSectionCompleted = useCallback(
    (section: SectionType) => {
      return sectionRecordingState.recordedSections.some(
        (s) => s.sectionName === section && s.uploadStatus === 'completed'
      );
    },
    [sectionRecordingState.recordedSections]
  );

  /**
   * Get completed recordings for a section
   */
  const getCompletedRecordings = useCallback(
    (section: SectionType) => {
      return sectionRecordingState.recordedSections.filter(
        (s) => s.sectionName === section && s.uploadStatus === 'completed'
      );
    },
    [sectionRecordingState.recordedSections]
  );

  /**
   * Clear completed recordings for a section (to allow re-recording)
   */
  const clearCompleted = useCallback(
    (section: SectionType) => {
      dispatch(clearCompletedSection(section));
    },
    [dispatch]
  );

  /**
   * Cleanup all recordings on unmount
   */
  useEffect(() => {
    return () => {
      sectionRecordingService.cleanupAll();
    };
  }, []);

  return {
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getSectionStatus,
    getSectionError,
    isAnyRecording,
    getActiveSection,
    isSectionCompleted,
    getCompletedRecordings,
    clearCompleted,
    activeRecording: sectionRecordingState.activeRecording,
    recordedSections: sectionRecordingState.recordedSections,
    hasContext: !!sectionRecordingState.scribeJobId,
  };
};
